/**
 * PropertyDetailTracker
 *
 * Client-side component that fires performance-marketing events for the
 * property detail page.  All events are pushed to both GA4 (gtag) and
 * the GTM dataLayer so they can be used as conversion signals, audience
 * triggers, and optimisation goals across Google Ads, Meta, etc.
 *
 * Events fired:
 *  - view_item              (page load — remarketing / retargeting)
 *  - property_detail_view   (custom, richer payload for GTM)
 *  - scroll_depth           (25 / 50 / 75 / 100 %) — RR uses depth_percentage
 *  - engagement_ping        (15 / 30 / 60 / 120 s) — RR uses engaged_time_ms
 *  - section_view           (each major content section enters viewport)
 */

'use client';

import { useEffect, useRef, useCallback } from 'react';
import { analyticsEvents } from '@/components/analytics/GoogleAnalytics';
import { fireRREvent } from '@/components/analytics/RouteRoyalTracker';
import { VISITOR_ID_COOKIE } from '@/lib/attribution';

interface PropertyDetailTrackerProps {
  propertyId: string;
  propertyName: string;
  price: number | null;
  currency: string;
  listingType?: string | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  location?: string | null;
  agentName?: string | null;
  /**
   * The listing entity behind this property page, when known. Emitting a
   * `listing_view` with the `listing_id` lets per-listing analytics populate
   * alongside per-property analytics.
   */
  listingId?: string | null;
}

const SCROLL_THRESHOLDS = [25, 50, 75, 100] as const;
const ENGAGEMENT_THRESHOLDS_SEC = [15, 30, 60, 120] as const;

const SECTION_SELECTORS = [
  { id: 'overview', selector: '[data-track-section="overview"]' },
  { id: 'features', selector: '[data-track-section="features"]' },
  { id: 'amenities', selector: '[data-track-section="amenities"]' },
  { id: 'gallery', selector: '[data-track-section="gallery"]' },
  { id: 'location', selector: '[data-track-section="location"]' },
  { id: 'contact', selector: '[data-track-section="contact"]' },
] as const;

function getVisitorId(): string | undefined {
  if (typeof document === 'undefined') return undefined;
  const match = document.cookie.match(new RegExp(`(?:^|; )${VISITOR_ID_COOKIE}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

function pushToDataLayer(payload: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(payload);
  }
}

function fireGtagEvent(name: string, params: Record<string, unknown>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', name, params);
  }
}

export function PropertyDetailTracker({
  propertyId,
  propertyName,
  price,
  currency,
  listingType,
  bedrooms,
  bathrooms,
  location,
  agentName,
  listingId,
}: PropertyDetailTrackerProps) {
  const scrollMilestones = useRef(new Set<number>());
  const engagementMilestones = useRef(new Set<number>());
  const viewedSections = useRef(new Set<string>());
  const pageLoadTime = useRef(Date.now());

  const itemPayload = useCallback(() => {
    const visitorId = getVisitorId();
    return {
      item_id: propertyId,
      item_name: propertyName,
      ...(price != null && { value: price }),
      currency,
      item_category: 'property',
      ...(listingType && { item_variant: listingType }),
      ...(bedrooms != null && { bedrooms }),
      ...(bathrooms != null && { bathrooms }),
      ...(location && { location }),
      ...(agentName && { agent_name: agentName }),
      ...(visitorId && { visitor_id: visitorId }),
    };
  }, [propertyId, propertyName, price, currency, listingType, bedrooms, bathrooms, location, agentName]);

  useEffect(() => {
    analyticsEvents.viewProperty(propertyId, propertyName, price ?? undefined);

    // Also emit a first-party listing view so per-listing analytics populate
    // for the listing entity backing this property page.
    if (listingId) {
      fireRREvent('listing_view', { listing_id: listingId, property_id: propertyId });
    }

    const detail = itemPayload();
    pushToDataLayer({ event: 'property_detail_view', ...detail });
    fireGtagEvent('property_detail_view', detail);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (docHeight <= 0) return;

      const pct = Math.round((scrollTop / docHeight) * 100);

      for (const threshold of SCROLL_THRESHOLDS) {
        if (pct >= threshold && !scrollMilestones.current.has(threshold)) {
          scrollMilestones.current.add(threshold);
          const params = {
            percent_scrolled: threshold,
            depth_percentage: threshold,
            ...itemPayload(),
            event_category: 'engagement',
          };
          fireGtagEvent('scroll_depth', params);
          pushToDataLayer({ event: 'scroll_depth', ...params });
          // Canonical RR contract: depth_percentage (matches SDK + AssetAnalyticsService)
          fireRREvent('scroll_depth', {
            property_id: propertyId,
            depth_percentage: threshold,
            ...(listingId ? { listing_id: listingId } : {}),
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [itemPayload, propertyId, listingId]);

  useEffect(() => {
    const timers = ENGAGEMENT_THRESHOLDS_SEC.map((sec) =>
      setTimeout(() => {
        if (engagementMilestones.current.has(sec)) return;
        engagementMilestones.current.add(sec);
        const engagedTimeMs = sec * 1000;
        const params = {
          engagement_time_sec: sec,
          engaged_time_ms: engagedTimeMs,
          ...itemPayload(),
          event_category: 'engagement',
        };
        // Keep GA/GTM event name for existing tags; RR uses canonical engagement_ping
        fireGtagEvent('engagement_time', params);
        pushToDataLayer({ event: 'engagement_time', ...params });
        fireRREvent('engagement_ping', {
          property_id: propertyId,
          engaged_time_ms: engagedTimeMs,
          ...(listingId ? { listing_id: listingId } : {}),
        });
      }, sec * 1000),
    );

    return () => timers.forEach(clearTimeout);
  }, [itemPayload, propertyId, listingId]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const sectionId = (entry.target as HTMLElement).dataset.trackSection;
          if (!sectionId || viewedSections.current.has(sectionId)) continue;

          viewedSections.current.add(sectionId);
          const timeOnPage = Math.round((Date.now() - pageLoadTime.current) / 1000);
          const params = {
            section_name: sectionId,
            time_to_section_sec: timeOnPage,
            ...itemPayload(),
            event_category: 'engagement',
          };
          fireGtagEvent('section_view', params);
          pushToDataLayer({ event: 'section_view', ...params });
          fireRREvent('section_view', {
            property_id: propertyId,
            section_name: sectionId,
            time_to_section_sec: timeOnPage,
            ...(listingId ? { listing_id: listingId } : {}),
          });
        }
      },
      { threshold: 0.3 },
    );

    const timer = setTimeout(() => {
      for (const { selector } of SECTION_SELECTORS) {
        const el = document.querySelector(selector);
        if (el) observer.observe(el);
      }
    }, 500);

    return () => {
      clearTimeout(timer);
      observer.disconnect();
    };
  }, [itemPayload, propertyId, listingId]);

  return null;
}
